import { useState } from "react";
import { Upload, X, FileText, Camera } from "lucide-react";
import { CameraModal } from "@/components/CameraModal";
import { toast } from "sonner";

interface FileUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  required?: boolean;
  inputId?: string;
  showCamera?: boolean;
}

export function FileUpload({
  files,
  onFilesChange,
  required = true,
  inputId = "file-upload-distribution",
  showCamera = true
}: FileUploadProps) {
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = event.target.files;
    if (newFiles) {
      onFilesChange([...files, ...Array.from(newFiles)]);
    }
    event.target.value = "";
  };

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const getFilePreview = (file: File) => {
    if (file.type.startsWith("image/")) {
      return URL.createObjectURL(file);
    }
    return null;
  };

  const handleCameraCapture = (file: File) => {
    onFilesChange([...files, file]);
    toast.success("Foto capturada com sucesso!");
  };

  return (
    <div className="mt-4 p-3 border rounded-lg">
      <label className="block text-sm font-medium mb-2">
        Arquivos: {required && <span className="text-red-500">*</span>}
      </label>

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 w-full">
          <label
            htmlFor={inputId}
            className="flex flex-col items-center justify-center flex-1 h-24 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-500"
          >
            <div className="flex flex-col items-center justify-center pt-2 pb-2">
              <Upload className="w-6 h-6 mb-1 text-gray-500 dark:text-gray-400" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <span className="font-semibold">Clique para selecionar</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                ou arraste os arquivos aqui
              </p>
            </div>
            <input
              id={inputId}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            />
          </label>

          {showCamera && (
            <button
              type="button"
              onClick={() => setIsCameraOpen(true)}
              className="flex flex-col items-center justify-center h-24 w-24 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-500 transition-colors"
              title="Tirar foto"
            >
              <Camera className="w-6 h-6 mb-1 text-gray-500 dark:text-gray-400" />
              <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold">
                Tirar foto
              </p>
            </button>
          )}
        </div>

        {/* Preview dos arquivos selecionados */}
        {files.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">
              Arquivos selecionados ({files.length}):
            </p>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
              {files.map((file, index) => {
                const preview = getFilePreview(file);
                return (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 bg-gray-100 dark:bg-gray-700 rounded-md"
                  >
                    {preview ? (
                      <img
                        src={preview}
                        alt={file.name}
                        className="w-10 h-10 object-cover rounded"
                      />
                    ) : (
                      <FileText className="w-10 h-10 text-gray-500" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded-full"
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <CameraModal
        isOpen={isCameraOpen}
        onClose={() => setIsCameraOpen(false)}
        onCapture={handleCameraCapture}
      />
    </div>
  );
}
